/*
 * AqeeLab Nutrition - Recherche maison (aqee-search)
 * --------------------------------------------------------------------------
 * <aqee-search>       : loupe de l'en-tête + fenêtre de recherche prédictive
 * <aqee-search-page>  : compléments de la page /search (compteurs des onglets,
 *                       collections correspondantes, surlignage)
 *
 * Le rendu HTML vient des sections Liquid aqee-predictive-search et
 * aqee-search-empty (API de rendu de section) : ce fichier charge, affiche,
 * surligne et gère le clavier.
 *
 * Complément « dans le mot » : Shopify ne trouve un mot que par son début
 * (« biot » ne trouve pas « probiotiques »). On charge donc une fois par visite
 * un index léger des titres (templates/search.aqee-index.liquid) et on ajoute
 * en tête les articles et produits que Shopify ne peut pas trouver.
 */
(() => {
  if (customElements.get('aqee-search')) return;

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 160;
  const RECENT_KEY = 'aqee-search-recent';
  const RECENT_MAX = 6;
  const SUGGEST_PARAMS = {
    section_id: 'aqee-predictive-search',
    'resources[type]': 'query,product,collection,article,page',
    'resources[limit]': '8',
    'resources[limit_scope]': 'each',
    'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
    'resources[options][unavailable_products]': 'last',
    'resources[options][prefix]': 'last',
  };

  /* Partagés entre les deux instances de l'en-tête (mobile + desktop) */
  const resultsCache = new Map();
  let emptyHtmlPromise = null;

  /* ---------------------------------------------------------------------- */
  /* Utilitaires                                                             */
  /* ---------------------------------------------------------------------- */

  const fold = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  /* Petits mots ignorés pour le surlignage et les comparaisons (« frais de port »
     ne doit pas surligner chaque « de » de la page). */
  const STOP_WORDS = new Set(['de', 'des', 'du', 'la', 'le', 'les', 'un', 'une', 'et', 'ou', 'en', 'au', 'aux', 'a', 'pour', 'par', 'sur', 'avec', 'sans', 'dans', 'ce', 'ces', 'mon', 'ma', 'mes', 'ton', 'ta', 'tes', 'son', 'sa', 'ses', 'est', 'qui', 'que', 'quel', 'quelle', 'quels', 'quelles', 'comment', 'pourquoi']);

  function tokensOf(query) {
    return [...new Set(fold(query).split(/[\s\-'’,.]+/).filter((t) => t.length >= 2 && !STOP_WORDS.has(t)))].sort((a, b) => b.length - a.length);
  }

  /* La recherche vise-t-elle une page ? Oui si le titre d'une page contient
     tous les mots importants tapés (« frais de port » -> « Frais de port & délais »). */
  function isPageIntent(titles, query) {
    const tokens = tokensOf(query).filter((t) => t.length >= 3);
    if (!tokens.length) return false;
    return titles.some((title) => {
      const t = fold(title);
      return tokens.every((token) => t.includes(token));
    });
  }

  /* Le texte contient-il tous les mots importants tapés ? */
  function mentionsAll(text, query) {
    const t = fold(text || '');
    const tokens = tokensOf(query).filter((tk) => tk.length >= 3);
    return tokens.length > 0 && tokens.every((token) => t.includes(token));
  }

  /* Le titre contient-il au moins un des mots importants tapés ? */
  function mentionsAny(text, query) {
    const t = fold(text || '');
    return tokensOf(query).filter((tk) => tk.length >= 3).some((token) => t.includes(token));
  }

  /* Surligne les mots tapés dans les éléments [data-aqs-hl], sans tenir compte
     des accents ni de la casse (« magnesium » surligne « Magnésium »). */
  function highlight(root, query) {
    const tokens = tokensOf(query);
    if (!tokens.length) return;
    /* Au milieu d'un mot (« biot » dans « probiotiques ») : seulement pour une
       recherche d'un seul mot, sinon « port » surligne « transport ». */
    const midWord = tokens.length === 1;

    root.querySelectorAll('[data-aqs-hl]').forEach((el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        if (!walker.currentNode.parentElement.closest('mark')) nodes.push(walker.currentNode);
      }

      nodes.forEach((node) => {
        const text = node.nodeValue;
        let folded = '';
        const map = [];
        for (let i = 0; i < text.length; i++) {
          const f = fold(text[i]);
          for (let k = 0; k < f.length; k++) map.push(i);
          folded += f;
        }

        const ranges = [];
        tokens.forEach((token) => {
          let from = 0;
          let at;
          while ((at = folded.indexOf(token, from)) !== -1) {
            const startsWord = at === 0 || !/[a-z0-9]/.test(folded[at - 1]);
            if (startsWord || (midWord && token.length >= 3)) ranges.push([map[at], map[at + token.length - 1] + 1]);
            from = at + token.length;
          }
        });
        if (!ranges.length) return;

        ranges.sort((a, b) => a[0] - b[0]);
        const merged = [ranges[0]];
        ranges.slice(1).forEach((r) => {
          const last = merged[merged.length - 1];
          if (r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
          else merged.push(r);
        });

        const frag = document.createDocumentFragment();
        let cursor = 0;
        merged.forEach(([start, end]) => {
          if (start > cursor) frag.append(text.slice(cursor, start));
          const mark = document.createElement('mark');
          mark.textContent = text.slice(start, end);
          frag.append(mark);
          cursor = end;
        });
        if (cursor < text.length) frag.append(text.slice(cursor));
        node.replaceWith(frag);
      });
    });
  }

  const recent = {
    read() {
      try {
        const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(list) ? list.filter((q) => typeof q === 'string') : [];
      } catch (e) {
        return [];
      }
    },
    add(query) {
      const q = (query || '').trim();
      if (q.length < MIN_CHARS) return;
      const list = recent.read().filter((item) => fold(item) !== fold(q));
      list.unshift(q);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (e) {
        /* navigation privée : on ignore */
      }
    },
    clear() {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch (e) {
        /* rien */
      }
    },
  };

  /* ---------------------------------------------------------------------- */
  /* Index « dans le mot »                                                    */
  /* ---------------------------------------------------------------------- */

  const INDEX_KEY = 'aqee-search-index-v1';
  const INDEX_TTL = 6 * 60 * 60 * 1000;
  let indexPromise = null;

  function loadIndex(searchUrl) {
    if (indexPromise) return indexPromise;
    try {
      const cached = JSON.parse(sessionStorage.getItem(INDEX_KEY) || 'null');
      if (cached && Date.now() - cached.at < INDEX_TTL && Array.isArray(cached.items)) {
        indexPromise = Promise.resolve(prepareIndex(cached.items));
        return indexPromise;
      }
    } catch (e) {
      /* stockage indisponible : on recharge */
    }
    indexPromise = fetch(`${searchUrl}?view=aqee-index`)
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => {
        try {
          sessionStorage.setItem(INDEX_KEY, JSON.stringify({ at: Date.now(), items }));
        } catch (e) {
          /* quota ou navigation privée */
        }
        return prepareIndex(items);
      })
      .catch(() => {
        indexPromise = null;
        return [];
      });
    return indexPromise;
  }

  function prepareIndex(items) {
    return items.map((item) => ({ ...item, ft: fold(item.t || ''), fk: fold(`${item.k || ''} ${item.e || ''}`) }));
  }

  const startsWordAt = (text, at) => at === 0 || !/[a-z0-9]/.test(text[at - 1]);

  function occursMidWordOnly(text, token) {
    let from = 0;
    let at;
    let found = false;
    while ((at = text.indexOf(token, from)) !== -1) {
      if (startsWordAt(text, at)) return false;
      found = true;
      from = at + 1;
    }
    return found;
  }

  /* Éléments qui contiennent tous les mots tapés, dont au moins un en milieu
     de mot (les seuls que Shopify ne sait pas trouver). Titres d'abord. */
  function infixMatches(index, query) {
    const tokens = tokensOf(query).filter((t) => t.length >= 3);
    if (!tokens.length) return [];
    return index
      .map((item) => {
        const hay = `${item.ft} ${item.fk}`;
        if (!tokens.every((t) => hay.includes(t))) return null;
        if (!tokens.some((t) => occursMidWordOnly(hay, t))) return null;
        const score = tokens.reduce((sum, t) => sum + (item.ft.includes(t) ? 2 : 1), 0);
        return { item, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .map((m) => m.item);
  }

  /* Mots complets des titres qui contiennent le mot tapé : « biot » -> « probiotiques » */
  function infixWords(index, query) {
    const tokens = tokensOf(query).filter((t) => t.length >= 3);
    if (tokens.length !== 1) return [];
    const token = tokens[0];
    const counts = new Map();
    index.forEach((item) => {
      (item.t || '').split(/[\s,.:;!?()«»"’'/-]+/).forEach((word) => {
        const f = fold(word);
        if (f.length > token.length && f.includes(token) && !f.startsWith(token)) {
          const key = f.replace(/s$/, '');
          const entry = counts.get(key) || { word: word.toLowerCase(), n: 0 };
          entry.n += 1;
          counts.set(key, entry);
        }
      });
    });
    return [...counts.values()].sort((a, b) => b.n - a.n).slice(0, 3).map((e) => e.word);
  }

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const pathOf = (url) => {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (e) {
      return url;
    }
  };

  function priceHtml(item) {
    const from = item.x && item.x.startsWith('dès ');
    const price = from ? item.x.slice(4) : item.x;
    return `<span class="aqs-product__price">${from ? '<span class="aqs-product__from">dès</span> ' : ''}<strong>${esc(price)}</strong>${item.c ? `<s>${esc(item.c)}</s>` : ''}</span>`;
  }

  function productCard(item) {
    const rating = Number(item.r) && Number(item.n)
      ? `<span class="aqs-product__rating" aria-label="Note ${esc(item.r)} sur 5"><span class="aqs-stars" style="--aqs-rating: ${Number(item.r)};" aria-hidden="true"></span><span>(${esc(item.n)})</span></span>`
      : '';
    return `<li><a class="aqs-product${item.s ? ' aqs-product--soldout' : ''}" href="${esc(item.u)}" data-aqs-item role="option">
      <span class="aqs-product__media">${item.i ? `<img src="${esc(item.i)}" alt="" loading="lazy" width="160" height="160">` : ''}${item.s ? '<span class="aqs-badge aqs-badge--muted">Épuisé</span>' : ''}</span>
      <span class="aqs-product__body">${item.e ? `<span class="aqs-product__type">${esc(item.e)}</span>` : ''}<span class="aqs-product__title" data-aqs-hl>${esc(item.t)}</span>${rating}${priceHtml(item)}</span>
    </a></li>`;
  }

  function articleRow(item) {
    return `<li><a class="aqs-article" href="${esc(item.u)}" data-aqs-item role="option">
      <span class="aqs-article__media">${item.i ? `<img src="${esc(item.i)}" alt="" loading="lazy" width="120" height="80">` : ''}</span>
      <span class="aqs-article__body"><span class="aqs-article__title" data-aqs-hl>${esc(item.t)}</span><span class="aqs-article__excerpt">Article du ${esc(item.d)}</span></span>
    </a></li>`;
  }

  function articleCard(item) {
    return `<li><a class="aqsp-article" href="${esc(item.u)}">
      <span class="aqsp-article__media">${item.i ? `<img src="${esc(item.i.replace('width=240', 'width=720'))}" alt="" loading="lazy">` : ''}</span>
      <span class="aqsp-article__body"><span class="aqsp-article__meta">${esc(item.d)}</span><span class="aqsp-article__title" data-aqs-hl>${esc(item.t)}</span><span class="aqsp-article__cta">Lire l'article</span></span>
    </a></li>`;
  }

  function sectionMarkup(html, sectionId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.getElementById(`shopify-section-${sectionId}`) || doc.body;
    return section.innerHTML;
  }

  const isVisible = (el) => !!(el && el.getClientRects().length);

  /* ---------------------------------------------------------------------- */
  /* <aqee-search> : la fenêtre de recherche de l'en-tête                    */
  /* ---------------------------------------------------------------------- */

  let instanceCount = 0;

  class AqeeSearch extends HTMLElement {
    connectedCallback() {
      this.uid = `aqs${++instanceCount}`;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-aqs-form]');
      this.input = this.querySelector('[data-aqs-input]');
      this.clearButton = this.querySelector('[data-aqs-clear]');
      this.emptyPane = this.querySelector('[data-aqs-empty]');
      this.outputPane = this.querySelector('[data-aqs-output]');
      this.status = this.querySelector('[data-aqs-status]');
      this.body = this.querySelector('[data-aqs-body]');
      this.trigger = this.querySelector('[data-aqs-open]');
      this.activeIndex = -1;
      this.lastQuery = '';
      this.timer = null;
      this.controller = null;

      this.input.setAttribute('aria-controls', `${this.uid}-body`);
      this.body.id = `${this.uid}-body`;

      this.trigger.addEventListener('click', () => this.open());
      this.querySelector('[data-aqs-close]').addEventListener('click', () => this.close());
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', () => recent.add(this.input.value));
      this.dialog.addEventListener('click', (event) => this.onResultClick(event));

      if (this.hasAttribute('data-shortcut') && !AqeeSearch.shortcutBound) {
        AqeeSearch.shortcutBound = true;
        document.addEventListener('keydown', AqeeSearch.onShortcut);
      }
    }

    static onShortcut(event) {
      const target = event.target;
      const typing = target.closest && target.closest('input, textarea, select, [contenteditable="true"]');
      const isSlash = event.key === '/' && !typing;
      const isCmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isSlash && !isCmdK) return;
      const instance = [...document.querySelectorAll('aqee-search')].find((el) => isVisible(el.trigger));
      if (!instance || instance.dialog.open) return;
      event.preventDefault();
      instance.open();
    }

    open() {
      if (this.dialog.open) return;
      /* L'en-tête rend la recherche dans des conteneurs masqués selon l'écran
         (mobile / desktop) : on sort la fenêtre au niveau de la page. */
      if (this.dialog.parentElement !== document.body) document.body.append(this.dialog);
      this.dialog.showModal();
      document.documentElement.classList.add('aqs-lock');
      this.loadEmptyState();
      loadIndex(this.dataset.searchUrl);
      this.input.focus();
      if (this.input.value.trim()) {
        this.input.select();
        this.onInput(true);
      }
    }

    close() {
      if (!this.dialog.open) return;
      if (this.controller) this.controller.abort();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const finish = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.documentElement.classList.remove('aqs-lock');
        if (isVisible(this.trigger)) this.trigger.focus({ preventScroll: true });
      };
      if (reduce) return finish();
      this.dialog.classList.add('is-closing');
      setTimeout(finish, 180);
    }

    /* --- Avant la saisie ------------------------------------------------ */

    loadEmptyState() {
      if (!emptyHtmlPromise) {
        const url = `${this.dataset.rootUrl.replace(/\/$/, '')}/?section_id=aqee-search-empty`;
        emptyHtmlPromise = fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => (html ? sectionMarkup(html, 'aqee-search-empty') : ''))
          .catch(() => {
            emptyHtmlPromise = null;
            return '';
          });
      }
      if (this.emptyPane.dataset.loaded) return this.renderRecent();
      emptyHtmlPromise.then((html) => {
        if (!html) return;
        this.emptyPane.innerHTML = html;
        this.emptyPane.dataset.loaded = 'true';
        this.renderRecent();
      });
    }

    renderRecent() {
      const box = this.emptyPane.querySelector('[data-aqs-recent]');
      if (!box) return;
      const list = recent.read();
      const ul = box.querySelector('[data-aqs-recent-list]');
      ul.innerHTML = '';
      list.forEach((q) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'aqs-chip aqs-chip--recent';
        a.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
        a.dataset.aqsItem = '';
        a.dataset.aqsQuery = q;
        a.textContent = q;
        li.append(a);
        ul.append(li);
      });
      box.hidden = list.length === 0;
      const clear = box.querySelector('[data-aqs-recent-clear]');
      if (clear && !clear.dataset.bound) {
        clear.dataset.bound = 'true';
        clear.addEventListener('click', () => {
          recent.clear();
          this.renderRecent();
          this.input.focus();
        });
      }
    }

    /* --- Pendant la saisie ---------------------------------------------- */

    onInput(immediate = false) {
      const query = this.input.value.trim();
      this.clearButton.hidden = this.input.value.length === 0;
      clearTimeout(this.timer);

      if (query.length < MIN_CHARS) {
        if (this.controller) this.controller.abort();
        this.lastQuery = '';
        this.setLoading(false);
        this.showPane('empty');
        return;
      }
      if (immediate === true) return this.search(query);
      this.timer = setTimeout(() => this.search(query), DEBOUNCE_MS);
    }

    async search(query) {
      if (query === this.lastQuery && !this.outputPane.hidden) return;
      this.lastQuery = query;
      const key = fold(query);

      if (resultsCache.has(key)) return this.renderResults(resultsCache.get(key), query);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setLoading(true);

      const params = new URLSearchParams({ q: query, ...SUGGEST_PARAMS });
      try {
        const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = sectionMarkup(await response.text(), 'aqee-predictive-search');
        resultsCache.set(key, html);
        if (query === this.lastQuery) this.renderResults(html, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setLoading(false);
      }
    }

    renderResults(html, query) {
      this.setLoading(false);
      this.outputPane.innerHTML = html;
      this.applyPageIntent(query);
      highlight(this.outputPane, query);
      this.showPane('results');
      this.announce(query);
      this.augment(query);
    }

    announce(query) {
      const results = this.outputPane.querySelector('[data-aqs-results]');
      const total = results ? Number(results.dataset.total) : 0;
      this.status.textContent = total ? `${total} résultat${total > 1 ? 's' : ''} pour ${query}` : `Aucun résultat pour ${query}`;
    }

    /* Ajoute en tête ce que Shopify ne trouve pas (mot tapé au milieu d'un mot). */
    async augment(query) {
      const index = await loadIndex(this.dataset.searchUrl);
      if (query !== this.lastQuery || !index.length) return;
      const results = this.outputPane.querySelector('[data-aqs-results]');
      if (!results || results.dataset.augmented === query) return;
      results.dataset.augmented = query;

      const matches = infixMatches(index, query);
      const words = infixWords(index, query);
      this.promoteTitleMatches(results, query);
      if (!matches.length && !words.length) return;

      const shown = new Set([...results.querySelectorAll('a[href]')].map((a) => pathOf(a.getAttribute('href'))));
      const fresh = matches.filter((item) => !shown.has(pathOf(item.u)));
      const products = fresh.filter((item) => item.y === 'p');
      const articles = fresh.filter((item) => item.y === 'a');
      if (!products.length && !articles.length && !words.length) return;

      /* Aucun résultat Shopify : on remplace l'état vide par une grille. */
      let grid = results.querySelector('.aqs-results__grid');
      if (!grid) {
        results.querySelectorAll('.aqs-empty, .aqs-discover').forEach((el) => el.remove());
        grid = document.createElement('div');
        grid.className = 'aqs-results__grid aqs-results__grid--full';
        grid.innerHTML = '<div class="aqs-results__main"></div>';
        results.prepend(grid);
      }
      let main = grid.querySelector('.aqs-results__main');
      if (!main) {
        main = document.createElement('div');
        main.className = 'aqs-results__main';
        grid.append(main);
      }

      const ensureGroup = (name, title, listClass, before) => {
        let list = results.querySelector(`.aqs-group--${name} .${listClass}`);
        if (list) return list;
        const section = document.createElement('section');
        section.className = `aqs-group aqs-group--${name}`;
        section.innerHTML = `<h3 class="aqs-group__title">${title}</h3><ul class="${listClass}" role="group"></ul>`;
        const ref = before ? main.querySelector(before) : null;
        main.insertBefore(section, ref);
        return section.querySelector('ul');
      };

      if (products.length) {
        const list = ensureGroup('products', 'Produits', 'aqs-products', '.aqs-group--articles');
        list.insertAdjacentHTML('afterbegin', products.slice(0, 4).map(productCard).join(''));
        [...list.children].slice(8).forEach((li) => li.remove());
      }
      if (articles.length) {
        const list = ensureGroup('articles', 'Articles &amp; conseils', 'aqs-articles', null);
        list.insertAdjacentHTML('afterbegin', articles.slice(0, 4).map(articleRow).join(''));
        [...list.children].slice(6).forEach((li) => li.remove());
      }

      /* Suggestions : « biot » -> « probiotiques » */
      if (words.length) {
        let side = grid.querySelector('.aqs-results__side');
        let list = results.querySelector('.aqs-queries');
        if (!list) {
          if (!side) {
            side = document.createElement('div');
            side.className = 'aqs-results__side';
            grid.prepend(side);
            grid.classList.remove('aqs-results__grid--full');
          }
          const section = document.createElement('section');
          section.className = 'aqs-group aqs-group--queries';
          section.innerHTML = '<h3 class="aqs-group__title">Suggestions</h3><ul class="aqs-queries" role="group"></ul>';
          side.prepend(section);
          list = section.querySelector('ul');
        }
        const known = new Set([...list.querySelectorAll('[data-aqs-query]')].map((a) => fold(a.dataset.aqsQuery)));
        const icon = list.querySelector('.aqs-query__icon')?.outerHTML
          || '<svg class="aqs-query__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14"/></svg>';
        words.filter((w) => !known.has(fold(w))).forEach((w) => {
          list.insertAdjacentHTML('beforeend', `<li><a class="aqs-query" href="${this.dataset.searchUrl}?q=${encodeURIComponent(w)}&options%5Bprefix%5D=last" data-aqs-item data-aqs-query="${esc(w)}" role="option">${icon}<span data-aqs-hl>${esc(w)}</span></a></li>`);
        });
      }

      highlight(results, query);
      results.dataset.total = String(Number(results.dataset.total || 0) + products.length + articles.length);
      this.announce(query);
    }

    /* Recherche qui vise une page (« frais de port », « cgv », « contact ») :
       les pages passent en tête, et les produits dont le titre ne contient aucun
       des mots tapés sont retirés (Shopify les ajoute par approximation). */
    applyPageIntent(query) {
      const results = this.outputPane.querySelector('[data-aqs-results]');
      const pages = results && results.querySelector('.aqs-group--pages');
      if (!pages) return;
      const titles = [...pages.querySelectorAll('.aqs-page')].map((a) => a.textContent);
      if (!isPageIntent(titles, query)) return;

      const grid = results.querySelector('.aqs-results__grid');
      let main = grid.querySelector('.aqs-results__main');
      if (!main) {
        main = document.createElement('div');
        main.className = 'aqs-results__main';
        grid.append(main);
      }
      pages.classList.add('aqs-group--first');
      main.prepend(pages);

      results.querySelectorAll('.aqs-products > li').forEach((li) => {
        const title = li.querySelector('.aqs-product__title');
        if (!mentionsAny(title && title.textContent, query)) li.remove();
      });
      /* Articles : on ne garde que ceux qui parlent vraiment du sujet
         (titre ou extrait contenant tous les mots), pas « lait frais ». */
      results.querySelectorAll('.aqs-articles > li').forEach((li) => {
        if (!mentionsAll(li.textContent, query)) li.remove();
      });
      results.querySelectorAll('.aqs-group--products, .aqs-group--articles').forEach((group) => {
        if (!group.querySelector('ul > li')) group.remove();
      });
      const side = grid.querySelector('.aqs-results__side');
      if (side && !side.children.length) {
        side.remove();
        grid.classList.add('aqs-results__grid--full');
      }
    }

    /* Shopify classe parfois devant des résultats approchants (« biot » ->
       produits « Bio ») : ceux dont le titre contient vraiment le texte tapé
       remontent en tête, dans leur ordre d'origine. */
    promoteTitleMatches(results, query) {
      const tokens = tokensOf(query).filter((t) => t.length >= 3);
      if (!tokens.length) return;
      results.querySelectorAll('.aqs-products, .aqs-articles').forEach((list) => {
        const items = [...list.children];
        const hit = (li) => {
          const title = li.querySelector('.aqs-product__title, .aqs-article__title');
          const text = title ? fold(title.textContent) : '';
          return tokens.every((t) => text.includes(t));
        };
        const sorted = [...items.filter(hit), ...items.filter((li) => !hit(li))];
        if (sorted.some((li, i) => li !== items[i])) list.append(...sorted);
      });
    }

    showPane(name) {
      const results = name === 'results';
      this.outputPane.hidden = !results;
      this.emptyPane.hidden = results;
      this.input.setAttribute('aria-expanded', String(results));
      if (!results) this.renderRecent();
      this.setActive(-1);
      this.body.scrollTop = 0;
    }

    setLoading(on) {
      this.form.classList.toggle('is-loading', on);
    }

    /* --- Clavier et clics ----------------------------------------------- */

    items() {
      const pane = this.outputPane.hidden ? this.emptyPane : this.outputPane;
      return [...pane.querySelectorAll('[data-aqs-item]')].filter(isVisible);
    }

    setActive(index) {
      const items = this.items();
      items.forEach((el) => el.classList.remove('is-active'));
      this.activeIndex = index;
      if (index < 0 || !items[index]) {
        this.activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      const el = items[index];
      if (!el.id) el.id = `${this.uid}-opt-${index}-${Date.now()}`;
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', el.id);
    }

    onKeydown(event) {
      const items = this.items();
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!items.length) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        let next = this.activeIndex + step;
        if (next >= items.length) next = 0;
        if (next < -1) next = items.length - 1;
        this.setActive(next);
      } else if (event.key === 'Enter' && this.activeIndex > -1 && items[this.activeIndex]) {
        event.preventDefault();
        items[this.activeIndex].click();
      }
    }

    onResultClick(event) {
      const link = event.target.closest('a[data-aqs-item]');
      if (!link) return;
      const plainClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

      /* Suggestion, recherche populaire ou récente : on remplit le champ et on
         affiche les résultats dans la fenêtre, plutôt que de changer de page. */
      if (link.dataset.aqsQuery !== undefined && plainClick) {
        event.preventDefault();
        this.input.value = link.dataset.aqsQuery;
        this.input.focus();
        this.onInput(true);
        return;
      }
      recent.add(this.input.value || link.dataset.aqsQuery);
    }
  }

  customElements.define('aqee-search', AqeeSearch);

  /* ---------------------------------------------------------------------- */
  /* <aqee-search-page> : la page /search                                    */
  /* ---------------------------------------------------------------------- */

  class AqeeSearchPage extends HTMLElement {
    connectedCallback() {
      const terms = this.dataset.terms || '';
      if (terms) {
        recent.add(terms);
        highlight(this, terms);
        this.loadCounts(terms);
        this.loadFragments(terms).then(() => {
          this.applyPageIntent(terms);
          this.augment(terms);
        });
        this.loadCollections(terms);
      }
      this.querySelectorAll('[data-aqs-sort]').forEach((select) => {
        select.addEventListener('change', () => {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', select.value);
          url.searchParams.delete('page');
          window.location.href = url.toString();
        });
      });
    }

    /* Chaque onglet affiche son nombre de résultats (un petit appel par type).
       « Tout » = somme des trois, car la recherche mélangée de Shopify
       sous-compte les articles. */
    loadCounts(terms) {
      const types = ['product', 'article', 'page'];
      const counts = types.map((type) => {
        const params = new URLSearchParams({ q: terms, type, view: 'aqee-count', 'options[prefix]': 'last' });
        return fetch(`${this.dataset.searchUrl}?${params}`)
          .then((r) => (r.ok ? r.json() : { count: 0 }))
          .then((data) => Number(data.count) || 0)
          .catch(() => null);
      });

      Promise.all(counts).then((values) => {
        const byType = { all: values.reduce((sum, n) => sum + (n || 0), 0) };
        types.forEach((type, i) => (byType[type] = values[i]));

        this.querySelectorAll('[data-aqs-count]').forEach((badge) => {
          const count = byType[badge.dataset.aqsCount];
          if (count === null || count === undefined) return;
          badge.textContent = count;
          badge.hidden = false;
          const tab = badge.closest('[data-aqs-tab]');
          if (tab && count === 0 && !tab.classList.contains('is-current')) tab.classList.add('is-empty');
        });

        const summary = this.querySelector('[data-aqs-summary]');
        if (summary) {
          summary.innerHTML = byType.all
            ? `<strong>${byType.all}</strong> résultat${byType.all > 1 ? 's' : ''}`
            : 'Aucun résultat';
        }
      });
    }

    /* Onglet « Tout » : articles et pages chargés chacun par leur propre recherche. */
    loadFragments(terms) {
      const sections = [...this.querySelectorAll('[data-aqs-fragment]')];
      if (!sections.length) return Promise.resolve();

      const loads = sections.map((section) => {
        const params = new URLSearchParams({ q: terms, type: section.dataset.aqsFragment, view: 'aqee-fragment', 'options[prefix]': 'last' });
        return fetch(`${this.dataset.searchUrl}?${params}`)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aqs-fragment-results]');
            const count = list ? Number(list.dataset.count) : 0;
            if (!count) return 0;
            section.querySelector('[data-aqs-fragment-list]').replaceChildren(...list.children);
            const badge = section.querySelector('[data-aqs-fragment-count]');
            if (badge) badge.textContent = `(${count})`;
            highlight(section, terms);
            section.hidden = false;
            return count;
          })
          .catch(() => 0);
      });

      return Promise.all(loads).then((counts) => {
        const anyProduct = this.querySelector('[data-aqs-has-results]');
        const noResult = this.querySelector('[data-aqs-noresult]');
        if (noResult && !anyProduct && counts.every((n) => !n)) noResult.hidden = false;
      });
    }

    /* Onglet « Tout », recherche qui vise une page (« frais de port ») : la section
       Pages remonte en tête, et les produits passent sous les articles si aucun
       de leurs titres ne contient les mots tapés. */
    applyPageIntent(terms) {
      const pages = this.querySelector('[data-aqs-fragment="page"]');
      if (!pages || pages.hidden) return;
      const titles = [...pages.querySelectorAll('.aqsp-page__title')].map((el) => el.textContent);
      if (!isPageIntent(titles, terms)) return;

      const body = this.querySelector('.aqsp-body');
      const anchor = body.querySelector('[data-aqs-page-collections]') || body.querySelector('.aqsp-tabs');
      anchor.after(pages);

      /* Articles hors sujet (titre et extrait sans tous les mots) masqués ;
         ils restent accessibles dans l'onglet Articles. */
      const articles = this.querySelector('[data-aqs-fragment="article"]');
      if (articles && !articles.hidden) {
        articles.querySelectorAll('[data-aqs-fragment-list] > li').forEach((li) => {
          if (!mentionsAll(li.textContent, terms)) li.remove();
        });
        if (!articles.querySelector('[data-aqs-fragment-list] > li')) articles.hidden = true;
      }

      /* Produits sans aucun des mots dans leur titre : masqués aussi
         (onglet Produits toujours disponible). */
      const products = body.querySelector('[data-aqs-has-results]');
      if (products) {
        const names = [...products.querySelectorAll('a[href*="/products/"]')].map((a) => a.textContent);
        if (!names.some((name) => mentionsAny(name, terms))) products.hidden = true;
      }
    }

    /* Ce que Shopify ne trouve pas (mot tapé au milieu d'un mot, ex. « biot »
       dans « probiotiques ») est rangé à sa place : les articles en tête de la
       section Articles, les produits (cas rare) dans un encart en fin de page. */
    async augment(terms) {
      const index = await loadIndex(this.dataset.searchUrl);
      const type = this.dataset.type || 'all';
      if (!index.length || type === 'page') return;

      const shown = new Set([...this.querySelectorAll('.aqsp-body a[href]')].map((a) => pathOf(a.getAttribute('href'))));
      const wanted = (item) => type === 'all' || (type === 'product' ? item.y === 'p' : item.y === 'a');
      const fresh = infixMatches(index, terms).filter((item) => wanted(item) && !shown.has(pathOf(item.u)));
      if (!fresh.length) return;

      const body = this.querySelector('.aqsp-body');
      const articles = fresh.filter((item) => item.y === 'a').slice(0, 9);
      const products = fresh.filter((item) => item.y === 'p').slice(0, 8);
      const added = [];

      if (articles.length) {
        let list;
        if (type === 'all') {
          const section = this.querySelector('[data-aqs-fragment="article"]');
          list = section && section.querySelector('[data-aqs-fragment-list]');
          if (section) section.hidden = false;
        } else {
          list = body.querySelector('.aqsp-grid--articles');
        }
        if (!list) {
          /* Onglet Articles sans aucun résultat Shopify */
          const section = document.createElement('section');
          section.className = 'aqsp-group';
          section.innerHTML = '<ul class="aqsp-grid aqsp-grid--articles"></ul>';
          (body.querySelector('.aqsp-tabs')).after(section);
          list = section.querySelector('ul');
        }
        list.insertAdjacentHTML('afterbegin', articles.map(articleCard).join(''));
        added.push(...[...list.children].slice(0, articles.length));
      }

      if (products.length) {
        const box = document.createElement('section');
        box.className = 'aqsp-group aqsp-extra';
        box.innerHTML = `<div class="aqsp-group__head"><h2 class="aqsp-h2">Autres produits</h2></div><ul class="aqs-products aqs-products--discover">${products.map(productCard).join('')}</ul>`;
        box.querySelectorAll('[role]').forEach((el) => el.removeAttribute('role'));
        box.querySelectorAll('[data-aqs-item]').forEach((el) => el.removeAttribute('data-aqs-item'));
        const pagination = body.querySelector('.pagination-wrapper');
        if (pagination) pagination.before(box);
        else body.append(box);
        added.push(box);
      }

      added.forEach((el) => highlight(el, terms));

      /* Il y a finalement des résultats : on retire le message « rien trouvé ». */
      this.querySelectorAll('.aqsp-noresult, .aqsp-body > .aqs-discover, [data-aqs-noresult]').forEach((el) => {
        el.hidden = true;
      });
    }

    /* La recherche complète de Shopify ne renvoie pas les collections :
       on les récupère via la recherche prédictive. */
    loadCollections(terms) {
      const box = this.querySelector('[data-aqs-page-collections]');
      if (!box) return;
      const params = new URLSearchParams({
        q: terms,
        section_id: 'aqee-predictive-search',
        'resources[type]': 'collection',
        'resources[limit]': '6',
        'resources[options][prefix]': 'last',
      });
      fetch(`${this.dataset.suggestUrl}?${params}`)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = doc.querySelector('.aqs-collections');
          if (!list) return;
          list.querySelectorAll('[role]').forEach((el) => el.removeAttribute('role'));
          box.querySelector('[data-aqs-page-collections-list]').replaceChildren(...list.children);
          highlight(box, terms);
          box.hidden = false;
        })
        .catch(() => {});
    }
  }

  customElements.define('aqee-search-page', AqeeSearchPage);
})();
